import { Box } from '@amzn/meridian';
import type { FormContextType, RJSFSchema, StrictRJSFSchema, WidgetProps } from '@rjsf/utils';
import { DestinationUrlInput } from 'features/campaign/campaign.types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useCampaignConfig } from '../../../../../hooks/useCampaignConfig';
import { useRequiredFieldOnChange } from '../utils/useRequiredFieldOnChange';
import { LinkPreview } from './components/LinkPreview';
import { LinkUrlSection } from './components/LinkUrlSection';
import { RefTagInput } from './components/RefTagInput';
import { useAutoGeneratedRefTag } from './hooks/useAutoGeneratedRefTag';
import { useUrlLogic } from './hooks/useUrlLogic';
import { generatePreviewUrl, parseUrl } from './utils';
import { getCreativeName } from './utils/formContextUtils';
import { generateRefTag } from './utils/refTagsUtils';

/**
 * LinkBuilder - Widget that manages concatenated URL + refTag in a single field
 * Used in schemas with single href field
 * Stores concatenated URL with refTag (e.g., '/kindle/ref=test123')
 */
export const LinkBuilder = <T = never, S extends StrictRJSFSchema = RJSFSchema, F extends FormContextType = never>(
  props: WidgetProps<T, S, F>,
) => {
  const {
    onChange,
    value,
    registry: { formContext },
    required,
  } = props;

  const campaignConfig = useCampaignConfig();

  const requiredFieldOnChange = useRequiredFieldOnChange(onChange, required);

  const parsedLinkComponents = useMemo(
    () => parseUrl(value, formContext?.platform),
    [
      value,
      formContext?.platform,
    ],
  );
  const lastSentUrlRef = useRef<string>(value || '');

  const creativeName = useMemo(
    () => getCreativeName(formContext),
    [
      formContext,
    ],
  );

  const { handleLinkTypeChange, handleLinkValueChange, handleShowVariationsChange, linkType, linkValue, showVariations, validationError } =
    useUrlLogic({
      platform: formContext?.platform,
      value,
    });

  const [refTag, setRefTag] = useState<string>(parsedLinkComponents.refTag);
  const [isAutoGenEnabled, setIsAutoGenEnabled] = useState<boolean>(() => {
    const { refTag: existingRefTag } = parsedLinkComponents;

    if (!existingRefTag) {
      return !value || value.trim() === '';
    }

    if (!creativeName) {
      return false;
    }

    return existingRefTag === generateRefTag(creativeName, campaignConfig.pageType);
  });

  const previewUrl = useMemo(
    () => (linkType && linkValue ? generatePreviewUrl(linkType, linkValue, refTag, formContext?.platform, showVariations) : ''),
    [
      linkType,
      linkValue,
      refTag,
      formContext?.platform,
      showVariations,
    ],
  );

  const handleRefTagChange = useCallback((newRefTag: string) => setRefTag(newRefTag), []);

  const handleAutoGenChange = useCallback((checked: boolean) => {
    setIsAutoGenEnabled(checked);
    if (!checked) setRefTag('');
  }, []);

  useAutoGeneratedRefTag({
    creativeName,
    isAutoGenEnabled,
    pageType: campaignConfig.pageType,
    setRefTag,
  });

  const handleLinkTypeChangeWithRefTag = useCallback(
    (newType: DestinationUrlInput) => {
      handleLinkTypeChange(newType);
      setRefTag('');
    },
    [
      handleLinkTypeChange,
    ],
  );

  useEffect(() => {
    if (previewUrl !== lastSentUrlRef.current) {
      lastSentUrlRef.current = previewUrl;
      requiredFieldOnChange(previewUrl);
    }
  }, [
    requiredFieldOnChange,
    previewUrl,
  ]);

  return (
    <Box>
      <LinkUrlSection
        linkType={linkType}
        linkValue={linkValue}
        onLinkTypeChange={handleLinkTypeChangeWithRefTag}
        onLinkValueChange={handleLinkValueChange}
        onShowVariationsChange={linkType === DestinationUrlInput.DEALS ? handleShowVariationsChange : undefined}
        showVariations={linkType === DestinationUrlInput.DEALS ? showVariations : undefined}
        validationError={validationError}
      />

      <Box spacingInset="400 none none">
        <RefTagInput
          isAutoGenEnabled={isAutoGenEnabled}
          onAutoGenChange={handleAutoGenChange}
          onChange={handleRefTagChange}
          value={refTag}
        />
      </Box>
      <Box spacingInset="400 none none">
        <LinkPreview marketplace={formContext?.marketplace} previewUrl={previewUrl} />
      </Box>
    </Box>
  );
};
